import styled from 'styled-components';
import { Skeleton } from 'antd';
import { LuSparkles } from 'react-icons/lu';
import {
	Chip,
	Chips,
} from '@routes/userRoutes/projects/videoProject/components/overlay/CountPanel';

/**
 * The AI's reading of the chosen video: what it seems to show, how the camera moves and the suggested way to count,
 * with the subject as a chip to change. A custom component: a glass card in the wizard's side panel (antd has no
 * equivalent); the chips are the count panel's.
 */

const tk = ({ theme }) => theme.token;

const Card = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).paddingSM}px ${tk(p).padding}px`};
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
`;

const Label = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => tk(p).marginXXS}px;
	font-size: ${p => tk(p).fontSizeXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${p => tk(p).colorPrimary};
`;

const Line = styled.div`
	font-size: ${p => tk(p).fontSizeSM}px;
	color: ${p => tk(p).colorText};
	b {
		font-weight: ${p => tk(p).fontWeightStrong};
	}
`;

const Small = styled.div`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
`;

const CAMERA = { hovering: 'drone hovering', moving: 'drone moving', fixed: 'fixed camera' };

const SuggestionCard = ({ status, suggestion, subject, onSubject, modeTitle }) => {
	if (status === 'looking')
		return (
			<Card aria-busy="true">
				<Label>
					<LuSparkles aria-hidden="true" /> Looking at your video…
				</Label>
				<Skeleton active title={false} paragraph={{ rows: 2 }} />
			</Card>
		);
	if (status !== 'ready' || !suggestion) return null;
	const s = suggestion;
	const chosen = subject || s.subject;
	const subjects = [s.subject, ...(s.alternatives || [])];
	return (
		<Card aria-label="AI suggestion">
			<Label>
				<LuSparkles aria-hidden="true" /> AI suggestion
			</Label>
			<Line>
				{s.confidence === 'low' ? 'Maybe ' : 'Looks like '}
				<b>{s.subject}</b> {s.scene}
			</Line>
			<Small>
				{s.in_view > 0 ? `About ${s.in_view} in view · ` : ''}
				{CAMERA[s.camera] || s.camera}
			</Small>
			{modeTitle && (
				<Line>
					Suggested way to count: <b>{modeTitle}</b>
				</Line>
			)}
			<Small>
				{s.confidence === 'low' ? 'Not sure. What do you want to count?' : 'Counting'}
			</Small>
			<Chips role="radiogroup" aria-label="What to count">
				{subjects.map(w => (
					<Chip
						key={w}
						type="button"
						role="radio"
						aria-checked={w === chosen}
						$active={w === chosen}
						onClick={() => onSubject?.(w)}>
						{w}
					</Chip>
				))}
			</Chips>
		</Card>
	);
};

export default SuggestionCard;
